const T = require("react").PropTypes;
const moment = require("moment");

const restrictions = {
    [1]: {
        Expression: /^-?\d*\.?\d*$/,
        Message: "Numbers only"
    },
    [2]: {
        Expression: /^[A-z\s\d]*$/,
        Message: "Letters, numbers, and spaces only"
    },
    [6]: {
        Expression: /^\d?\(?([0-9]{3})\)?[- ]?([0-9]{3})[- ]?([0-9]{4})$/,
        Message: "Please enter a valid phone number. Example: (111) 222-3333"
    },
    [7]: {
        Expression: /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
        Message: "Please enter a valid email. Example: imyself@cubesmart.com"
    },
    Default: {
        Expression: /.*/,
        Message: "The specified value is invalid"
    }
};

/**
 * A map of functions, each of which returning a sequence of 
 * messages describing what's wrong with a specified question.  
 * Questions with nothing wrong will have no messages.
 */
const validators = {
    // 0 - Freeform
    [0]: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        const selectionText = question.AnswerSelections[0].SelectionText;
        const RestrictionID = question.QuestionType.Restrictions;
        const restriction = restrictions[RestrictionID] || restrictions.Default;

        if (question.Required
            && (!question.AnswerSelections.length
                || !selectionText)) {
            result.push("Please enter an answer.");
        }

        if (!restriction.Expression.test(selectionText)) {
            result.push(restriction.Message);
        }

        return result;
    },
    // 3 - dropdown
    [3]: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        if (question.Required
            && (!question.AnswerSelections.length
                || !question.AnswerSelections[0].SelectionText)) {
            result.push("Please select an answer.");
        }

        return result;
    },
    // 6 - date
    [6]: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        if (question.Required) {
            if(!question.AnswerSelections.length) {
                result.push("Please enter a valid date.");
            }
            else {
                const text = question.AnswerSelections[0].SelectionText;

                const parsed = moment(text);

                if (!parsed.isValid()) {
                    result.push("Please enter a valid date.");
                }
            }
        }

        return result;
    },
    // 7 - checklist
    [7]: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        const actual = (question.AnswerSelections || []).filter(q => q.SelectionText).length;
        const min = question.QuestionType.MinAnswerSelectionCount;
        const max = question.QuestionType.MaxAnswerSelectionCount;

        if (actual < min) {
            const suffix = min === 1 ? "" : "es";

            result.push(`Please check at least ${min} box${suffix}.`);
        }

        if (actual > max) {
            const suffix = max === 1 ? "" : "es";

            result.push(`Please check no more than ${max} box${suffix}.`);
        }

        return result;
    },
    // 8 - table
    [8]: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        return result;
    },
    //101 - cauditList
    [101]: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        const numSelections = question.AnswerSelections.filter((selection) => {                
                return selection.SelectionText.some((text) => {
                    return text != "";
                });
            }).length;       

        if (numSelections > question.QuestionType.MaxAnswerSelectionCount) {
            const noun = question.QuestionType.MaxAnswerSelectionCount === 1 ? "entry" : "entries";

            result.push(`Please fill in no more than ${question.QuestionType.MaxAnswerSelectionCount} ${noun}.`);
        }

        if (numSelections < question.QuestionType.MinAnswerSelectionCount) {
            const noun = question.QuestionType.MinAnswerSelectionCount === 1 ? "entry" : "entries";

            result.push(`Please fill in at least ${question.QuestionType.MinAnswerSelectionCount} ${noun}.`);
        }

        return result;
    },
    // Everything else (i.e. freeform)
    Default: function (question) {
        let result = [];

        if (question.RequireComment && !question.AdditionalComment) {
            result.push("Please provide a comment.");
        }

        if (question.Required
            && (!question.AnswerSelections.length
                || !question.AnswerSelections[0].SelectionText)) {
            result.push("Please enter an answer.");
        }

        return result;
    }
};

const QuestionValidationStatus = {
    PropType: T.shape({
        IsValid: T.bool.isRequired,
        Messages: T.arrayOf(T.string).isRequired
    }),
    ForQuestion(question) {
        const questionTypeID = question.QuestionType.BaseDisplayType;

        const validate = validators[questionTypeID] || validators.Default;

        const messages = validate(question);

        return {
            IsValid: !messages.length,
            Messages: messages
        };
    },
    QuestionWithValidation(question) {
        const status = QuestionValidationStatus.ForQuestion(question);

        return Object.assign({}, question, {
            ValidationStatus: status
        });
    },
    SectionWithValidation(section) {
        const validatedQuestions = section.Questions.map(
            QuestionValidationStatus.QuestionWithValidation);

        return Object.assign({}, section, {
            Questions: validatedQuestions
        });
    },
    PageWithValidation(page) {
        const validatedSections = page.Sections.map(
            QuestionValidationStatus.SectionWithValidation);

        return Object.assign({}, page, {
            Sections: validatedSections
        });
    },
    ManyPagesWithValidation(pages) {
        return pages.map(QuestionValidationStatus.PageWithValidation);
    },
    QuestionIsPositivelyValid(question) {
        return question.hasOwnProperty("ValidationStatus")
            && question.ValidationStatus.IsValid;
    },
    SectionIsPositivelyValid(section) {
        return section.Questions
            .every(QuestionValidationStatus.QuestionIsPositivelyValid);
    },
    PageIsPositivelyValid(page) {
        return page.Sections
            .every(QuestionValidationStatus.SectionIsPositivelyValid);
    }
};

module.exports = QuestionValidationStatus;


// WEBPACK FOOTER //
// ./src/types/QuestionValidationStatus.js